import { ErrorBoundary as SolidErrorBoundary, type ParentComponent, Show } from 'solid-js';
import { RefreshCw, TriangleAlert } from 'lucide-solid';
import { GlassCard } from '~/shared/ui/GlassCard';
import { Button } from '~/shared/ui/Button';
import { useI18n } from '~/shared/lib/i18n';
import { isStaleChunkError, reloadOnceForNewVersion, staleChunkState } from '@sportpark/shared/stale-chunk';

const FallbackCard: ParentComponent<{ error: unknown; reset: () => void }> = (props) => {
  const { t } = useI18n();
  const message = props.error instanceof Error ? props.error.message : String(props.error);
  return (
    <div class="min-h-screen flex items-center justify-center p-6">
      <GlassCard strong class="max-w-[480px] w-full p-8 text-center">
        <div
          class="mx-auto mb-4 w-12 h-12 rounded-full flex items-center justify-center"
          style={{ background: 'hsl(4 90% 63% / 0.15)' }}
        >
          <TriangleAlert class="h-6 w-6" style={{ color: 'var(--color-danger)' }} />
        </div>
        <h1 class="text-[20px] font-semibold mb-2">{t('states.errorTitle')}</h1>
        <p class="text-[13px] text-secondary mb-1">{t('states.errorHint')}</p>
        <p class="text-[11px] font-mono text-tertiary mb-5 max-h-24 overflow-auto break-all">
          {message}
        </p>
        <div class="flex gap-2 justify-center">
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Перезагрузить
          </Button>
          <Button onClick={props.reset}>{t('common.retry')}</Button>
        </div>
      </GlassCard>
    </div>
  );
};

const NewVersionCard = () => (
  <div class="min-h-screen flex items-center justify-center p-6">
    <GlassCard strong class="max-w-[480px] w-full p-8 text-center">
      <div class="mx-auto mb-4 w-12 h-12 rounded-full flex items-center justify-center bg-white/5">
        <RefreshCw class="h-6 w-6 animate-spin text-secondary" />
      </div>
      <h1 class="text-[20px] font-semibold mb-2">Вышла новая версия — обновляем</h1>
      <p class="text-[13px] text-secondary">Страница перезагрузится сама через мгновение.</p>
    </GlassCard>
  </div>
);

const Fallback = (props: { error: unknown; reset: () => void }) => {
  const reloading = staleChunkState.reloading || (isStaleChunkError(props.error) && reloadOnceForNewVersion());
  return (
    <Show when={reloading} fallback={<FallbackCard error={props.error} reset={props.reset} />}>
      <NewVersionCard />
    </Show>
  );
};

export const AppErrorBoundary: ParentComponent = (props) => (
  <SolidErrorBoundary fallback={(error, reset) => <Fallback error={error} reset={reset} />}>
    {props.children}
  </SolidErrorBoundary>
);
